import React, { useState, useEffect } from 'react';
import { compressFiles, optimizeFormData, validateFormData } from '../../../shared/utils/performanceUtils';
import { apiGet, apiPost, apiPut, apiDelete } from '../../../shared/utils/apiConfig';
import { useTitle } from '../../../context/TitleContext';
import { useNavigate } from 'react-router-dom';
import { showSuccessToast, showErrorToast } from '../../../shared/utils/toastHelper';
import 'react-toastify/dist/ReactToastify.css';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaSave, FaPlus, FaTrash, FaUpload } from 'react-icons/fa';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PharmacyAddProduct = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        description: '',
        sub_category_id: '',
        images: []
    });
    const [variants, setVariants] = useState([
        { unit_id: '', price: '', quantity: '' }
    ]);
    const [subCategories, setSubCategories] = useState([]);
    const [units, setUnits] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [imageProcessing, setImageProcessing] = useState(false);


    useEffect(() => {
        setTitle('إضافة منتج جديد');
        fetchSubCategories();
        fetchUnits();
    }, [setTitle]);

    const getAuthToken = () => {
        const storeData = sessionStorage.getItem('storekhadamat');
        if (storeData) {
            try {
                const parsed = JSON.parse(storeData);
                return parsed.token;
            } catch {
                return null;
            }
        }
        return null;
    };

    const fetchSubCategories = async () => {
        try {
            const token = getAuthToken();
            const response = await axios.get(`${BASE_URL}/pharmacy/sub-categories`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });
            // التحقق من وجود البيانات مباشرة
            if (response.data.data && Array.isArray(response.data.data)) {
                setSubCategories(response.data.data);
            } else {
                setSubCategories([]);
            }
        } catch (error) {
            showErrorToast('فشل في جلب الفئات الفرعية');
        }
    };

    const fetchUnits = async () => {
        try {
            const token = getAuthToken();
            const response = await axios.get(`${BASE_URL}/category_unit`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });
            const list = response.data?.data || response.data?.result || [];
            setUnits(Array.isArray(list) ? list : []);
        } catch (error) {
            showErrorToast('فشل في جلب الوحدات');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageChange = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        setImageProcessing(true);

        try {
            const compressedFiles = await compressFiles(files, { maxSize: 800000, quality: 0.85 });

        setFormData(prev => ({
            ...prev,
                images: [...prev.images, ...compressedFiles]
        }));
        } catch (error) {
            showErrorToast('فشل في معالجة الصور');
        } finally {
            setImageProcessing(false);
        }
    };

    const removeImage = (index) => {
        setFormData(prev => ({
            ...prev,
            images: prev.images.filter((_, i) => i !== index)
        }));
    };

    const handleVariantChange = (index, field, value) => {
        const newVariants = [...variants];
        newVariants[index][field] = value;
        
        setVariants(newVariants);
    };

    const addVariant = () => {
        setVariants([...variants, { unit_id: '', price: '', quantity: '' }]);
    };

    const removeVariant = (index) => {
        if (variants.length > 1) {
            setVariants(variants.filter((_, i) => i !== index));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name.trim() || !formData.sub_category_id) {
            showErrorToast('يرجى ملء جميع الحقول المطلوبة');
            return;
        }

        if (formData.images.length === 0) {
            showErrorToast('يرجى إضافة صورة واحدة على الأقل للمنتج');
            return;
        }

        if (variants.some(v => !v.unit_id || !v.price || !v.quantity)) {
            showErrorToast('يرجى ملء جميع بيانات المتغيرات');
            return;
        }

        try {
            if (imageProcessing) {
                showErrorToast('يرجى الانتظار حتى يتم تجهيز الصور قبل الإرسال');
                return;
            }

            setSubmitting(true);
            const token = getAuthToken();
            
            if (!token) {
                showErrorToast('لا يوجد token للمصادقة');
                return;
            }

            // إعداد البيانات للإرسال
            const submitData = new FormData();
            submitData.append('name', formData.name.trim());
            submitData.append('code', formData.code.trim());
            submitData.append('description', formData.description.trim());
            submitData.append('sub_category_id', formData.sub_category_id);

            // إضافة الصور
            formData.images.forEach((image, index) => {
                submitData.append(`images[${index}]`, image);
            });

            // إضافة المتغيرات
            variants.forEach((variant, index) => {
                submitData.append(`variants[${index}][unit_id]`, variant.unit_id || '');
                submitData.append(`variants[${index}][price]`, variant.price || '');
                submitData.append(`variants[${index}][quantity]`, variant.quantity || '');
            });

      

       

            const response = await axios.post(`${BASE_URL}/pharmacy/products`, submitData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                    'Content-Type': 'multipart/form-data',
                },
            });


            if (response.data.status === 'failed' || response.data.status === 'faild') {
                throw new Error(response.data.message || 'فشل في إضافة المنتج');
            }

            showSuccessToast('تم إضافة المنتج بنجاح');
            navigate('/pharmacy-products');
        } catch (error) {
            
            // التعامل مع أخطاء الـ validation
            if (error.response?.data?.errors) {
                const errors = error.response.data.errors;
                // عرض أول خطأ
                const firstError = Object.values(errors)[0];
                if (Array.isArray(firstError) && firstError.length > 0) {
                    showErrorToast(firstError[0]);
                } else {
                    showErrorToast(error.response?.data?.message || 'فشل في إضافة المنتج');
                }
            } else {
                showErrorToast(error.response?.data?.message || error.message || 'فشل في إضافة المنتج');
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">جاري تحميل البيانات...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn btn-outline-secondary d-flex align-items-center"
                        onClick={() => navigate('/pharmacy-products')}
                        style={{ borderRadius: '8px' }}>
                        <FaArrowLeft className="mx-2" />
                        العودة
                    </motion.button>
                    <div></div>
                </div>

                {/* Add Form */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="card shadow-sm"
                    style={{ borderRadius: '12px' }}>
                    
                    <div className="card-body p-4">
                        <form onSubmit={handleSubmit}>
                            {/* Basic Info */}
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                        اسم المنتج *
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder="أدخل اسم المنتج"
                                        required
                                        style={{
                                            border: '1px solid #e9ecef',
                                            borderRadius: '8px',
                                            padding: '12px 16px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                        كود المنتج
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="code"
                                        value={formData.code}
                                        onChange={handleInputChange}
                                        placeholder="أدخل كود المنتج"
                                        style={{
                                            border: '1px solid #e9ecef',
                                            borderRadius: '8px',
                                            padding: '12px 16px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                        الفئة الفرعية *
                                    </label>
                                    <select
                                        className="form-control"
                                        name="sub_category_id"
                                        value={formData.sub_category_id}
                                        onChange={handleInputChange}
                                        required
                                        style={{
                                            border: '1px solid #e9ecef',
                                            borderRadius: '8px',
                                            padding: '12px 16px',
                                            fontSize: '14px'
                                        }}>
                                        <option value="">اختر الفئة الفرعية</option>
                                        {subCategories.map(category => (
                                            <option key={category.id} value={category.id}>
                                                {category.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-medium" style={{ color: '#333' }}>
                                    وصف المنتج
                                </label>
                                <textarea
                                    className="form-control"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    placeholder="أدخل وصف المنتج"
                                    rows="3"
                                    style={{
                                        border: '1px solid #e9ecef',
                                        borderRadius: '8px',
                                        padding: '12px 16px',
                                        fontSize: '14px'
                                    }}
                                />
                            </div>

                            {/* Images */}
                            <div className="mb-4">
                                <label className="form-label fw-medium" style={{ color: '#333' }}>
                                    صور المنتج 
                                </label>
                                <div className="mb-3">
                                    <input
                                        type="file"
                                        className="form-control"
                                        accept="image/*"
                                        multiple
                                        onChange={handleImageChange}
                                        disabled={imageProcessing}
                                        style={{
                                            border: '1px solid #e9ecef',
                                            borderRadius: '8px',
                                            padding: '12px 16px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>
                                
                                {imageProcessing && (
                                    <div className="alert alert-info py-2 px-3">
                                        جاري تجهيز الصور، يرجى الانتظار...
                                    </div>
                                )}
                                
                                {formData.images.length > 0 && (
                                    <div className="row">
                                        {formData.images.map((image, index) => (
                                            <div key={index} className="col-md-3 mb-2">
                                                <div className="position-relative">
                                                    <img
                                                        src={URL.createObjectURL(image)}
                                                        alt={`Preview ${index + 1}`}
                                                        className="img-fluid rounded"
                                                        style={{ width: '100%', height: '100px', objectFit: 'cover' }}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-danger btn-sm position-absolute top-0 end-0"
                                                        onClick={() => removeImage(index)}
                                                        style={{ padding: '2px 6px' }}>
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Variants */}
                            <div className="mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <label className="form-label fw-medium mb-0" style={{ color: '#333' }}>
                                        متغيرات المنتج *
                                    </label>
                                    <motion.button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={addVariant}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        style={{ borderRadius: '8px' }}>
                                        <FaPlus className="mx-1" />
                                        إضافة متغير
                                    </motion.button>
                                </div>

                                {variants.map((variant, index) => (
                                    <div key={index} className="card mb-3" style={{ borderRadius: '8px' }}>
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <h6 className="mb-0">متغير {index + 1}</h6>
                                                {variants.length > 1 && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-danger btn-sm"
                                                        onClick={() => removeVariant(index)}
                                                        style={{ borderRadius: '8px' }}>
                                                        <FaTrash />
                                                    </button>
                                                )}
                                            </div>
                                            
                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                                        الوحدة *
                                                    </label>
                                                    <select
                                                        className="form-control"
                                                        value={variant.unit_id}
                                                        onChange={(e) => handleVariantChange(index, 'unit_id', e.target.value)}
                                                        required
                                                        style={{
                                                            border: '1px solid #e9ecef',
                                                            borderRadius: '8px',
                                                            padding: '12px 16px',
                                                            fontSize: '14px'
                                                        }}>
                                                        <option value="">اختر الوحدة</option>
                                                        {units.map(unit => (
                                                            <option key={unit.id} value={unit.id}>
                                                                {unit.name || unit.title || unit.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-md-3 mb-3">
                                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                                        السعر *
                                                    </label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={variant.price}
                                                        onChange={(e) => handleVariantChange(index, 'price', e.target.value)}
                                                        placeholder="أدخل السعر"
                                                        required
                                                        min="1"
                                                        step="1"
                                                        style={{
                                                            border: '1px solid #e9ecef',
                                                            borderRadius: '8px',
                                                            padding: '12px 16px',
                                                            fontSize: '14px'
                                                        }}
                                                    />
                                                </div>
                                                <div className="col-md-3 mb-3">
                                                    <label className="form-label fw-medium" style={{ color: '#333' }}>
                                                        الكمية *
                                                    </label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={variant.quantity}
                                                        onChange={(e) => handleVariantChange(index, 'quantity', e.target.value)}
                                                        placeholder="أدخل الكمية"
                                                        required
                                                        min="0"
                                                        style={{
                                                            border: '1px solid #e9ecef',
                                                            borderRadius: '8px',
                                                            padding: '12px 16px',
                                                            fontSize: '14px'
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                            <div className="d-flex justify-content-center gap-2 mt-4">
                                <motion.button
                                    type="submit"
                                    className="btn btn-primary d-flex align-items-center"
                                    disabled={submitting || imageProcessing || formData.images.length === 0}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    style={{
                                        backgroundColor: '#184194',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '10px 24px'
                                    }}>
                                    <FaSave className="mx-2" />
                                    {submitting ? 'جاري الإضافة...' : 'إضافة المنتج'}
                                </motion.button>
                                
                                <motion.button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => navigate('/pharmacy-products')}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    style={{ 
                                        borderRadius: '8px',
                                        padding: '10px 24px'
                                    }}>
                                    إلغاء
                                </motion.button>
                            </div>
                        </form>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default PharmacyAddProduct;
